import { AnimatePresence, motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Reveal } from "./Reveal";
import { EASE } from "@/lib/site";

const AUDIENCES = {
  ci: {
    tab: "For EPC/Integrators",
    tag: "FOR EPC/INTEGRATORS / BTM",
    title: "Behind-the-meter optimisation for industrial sites",
    body: "We build custom digital twins for every site, ensuring optimal economic dispatch of energy resources for sites integrating solar, wind and energy storage behind the meter. Our solutions are tuned to your specific tariff and load profile.",
    features: [
      "Optimised solar/wind + storage dispatch",
      "Time-of-use tariff optimisation",
      "Dynamic load management & peak shaving",
      "Day-ahead energy market bidding algorithms",
      "Compliance & telemetry reporting",
    ],
    link: "/for-commercial-industrial",
    testid: "audience-panel-ci",
  },
  utilities: {
    tab: "For Utilities",
    tag: "FOR UTILITIES / DISCOMS",
    title: "Grid-side control for distribution utilities",
    body: "We make an opaque grid transparent. Visibility, control and optimisation for renewable-heavy distribution networks, from the substation down to the feeder, ensures that the grid is always performing at peak efficiency.",
    features: [
      "Feeder-level hosting capacity analysis",
      "Automated curtailment minimization",
      "Sub-second frequency response & battery dispatch",
      "Duck-curve mitigation and peak shaving",
    ],
    link: "/for-utilities",
    testid: "audience-panel-utilities",
  },
};

export const AudienceToggle = ({ audience, setAudience }) => {
  const active = AUDIENCES[audience];
  return (
    <section
      id="what-we-do"
      data-testid="audience-toggle-section"
      className="py-20 sm:py-28 lg:py-32 border-t border-[#E2DFD7]"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        <Reveal>
          <p className="font-mono text-xs uppercase tracking-[0.22em] text-[#2C4A3A] font-semibold">
            01 / What We Do
          </p>
          <h2 className="mt-6 text-2xl sm:text-3xl lg:text-4xl font-medium tracking-tight text-[#121413] max-w-2xl">
            Optimised to scale.
          </h2>
          <div className="mt-10 inline-flex flex-col sm:flex-row border border-[#C5C1B6] bg-[#FAF9F6]">
            {Object.entries(AUDIENCES).map(([key, a]) => (
              <button
                key={key}
                data-testid={`audience-tab-${key}`}
                onClick={() => setAudience(key)}
                className={`px-5 sm:px-6 py-3 font-mono text-[11px] sm:text-xs uppercase tracking-[0.16em] transition-colors duration-300 text-left sm:text-center ${
                  audience === key
                    ? "bg-[#121413] text-[#F5F4F0]"
                    : "text-[#4A4D4A] hover:text-[#121413]"
                }`}
              >
                {a.tab}
              </button>
            ))}
          </div>
        </Reveal>
        <div className="mt-10 border border-[#E2DFD7] bg-[#FAF9F6] overflow-hidden">
          <AnimatePresence mode="wait">
            <motion.article
              key={audience}
              data-testid={active.testid}
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.35, ease: EASE }}
              className="p-8 sm:p-10 lg:p-14 grid lg:grid-cols-2 gap-10 lg:gap-16"
            >
              <div className="flex flex-col items-start">
                <p className="font-mono text-[11px] uppercase tracking-[0.22em] text-[#B86A28]">
                  {active.tag}
                </p>
                <h3 className="mt-5 text-xl sm:text-2xl font-medium tracking-tight text-[#121413]">
                  {active.title}
                </h3>
                <p className="mt-4 text-base leading-relaxed text-[#4A4D4A]">{active.body}</p>
                <Link
                  data-testid={`${active.testid}-cta`}
                  to={active.link}
                  className="mt-8 inline-flex items-center gap-2 text-sm font-medium text-[#121413] border-b border-[#121413] pb-1 hover:text-[#2C4A3A] hover:border-[#2C4A3A] transition-colors duration-300 group"
                >
                  Explore the solution
                  <span className="transition-transform duration-300 group-hover:translate-x-1">→</span>
                </Link>
              </div>
              <ul className="space-y-4 self-center">
                {active.features.map((f) => (
                  <li key={f} className="flex items-start gap-3 text-sm sm:text-base text-[#4A4D4A]">
                    <span className="mt-2 w-1.5 h-1.5 shrink-0 bg-[#B86A28]" />
                    {f}
                  </li>
                ))}
              </ul>
            </motion.article>
          </AnimatePresence>
        </div>
      </div>
    </section>
  );
};
